import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { CATEGORIES, DAYS, IconFor } from "@/lib/constants";

function fmt(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "pm" : "am";
  const hr = ((h + 11) % 12) + 1;
  return `${hr}:${m.toString().padStart(2, "0")} ${period}`;
}

export default function PrintView() {
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/activities");
        setActivities(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const byDay = useMemo(() => {
    const map = Object.fromEntries(DAYS.map((d) => [d.key, []]));
    for (const a of activities) if (map[a.day]) map[a.day].push(a);
    for (const key of Object.keys(map)) map[key].sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [activities]);

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="no-print flex items-center justify-between max-w-6xl mx-auto mb-6">
        <Link to="/app/planner">
          <Button variant="outline" className="rounded-full" data-testid="print-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
        </Link>
        <Button onClick={() => window.print()} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="print-now-btn">
          <Printer className="w-4 h-4 mr-1" /> Print my week
        </Button>
      </div>

      <div className="max-w-6xl mx-auto">
        <h1 className="font-heading font-bold text-3xl text-slate-800 mb-6 text-center">My Week</h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 print-full">
          {DAYS.map((d) => (
            <div key={d.key} className="day-column bg-white border border-slate-300 rounded-xl p-3">
              <h2 className="font-heading font-bold text-slate-800 mb-2 border-b pb-1">{d.label}</h2>
              <div className="space-y-2">
                {byDay[d.key].length === 0 && (
                  <p className="text-xs text-slate-400 italic">—</p>
                )}
                {byDay[d.key].map((a) => {
                  const c = CATEGORIES[a.category] || CATEGORIES.routine;
                  return (
                    <div key={a.id} className={`activity-block ${c.bg} ${c.text} border ${c.border} rounded-lg p-2 print-block`}>
                      <div className="flex items-center gap-2 mb-1">
                        <IconFor name={a.icon} className="w-4 h-4" />
                        <span className="font-semibold text-sm">{a.title}</span>
                      </div>
                      <div className="text-xs">{fmt(a.start_time)} – {fmt(a.end_time)}</div>
                      {a.notes && <div className="text-xs mt-1 opacity-80">{a.notes}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
